import { useEffect, useRef, useState } from 'react';

/**
 * Hash-driven links/booking view state shared by the public provider page
 * and the dev fixture. The URL hash is the source of truth so browser
 * Back/Forward, reloads, and deep links all stay in sync with the render:
 *
 * - openBooking pushes a history entry with BOOKING_HASH
 * - popstate re-derives the view from the hash
 * - exitBooking pops our own entry when one exists, and falls back to
 *   replaceState for direct deep links where there's nothing of ours to pop
 */

export const BOOKING_HASH = '#booking';

export type PublicView = 'links' | 'booking';

export function useBookingHashView(basePath: () => string) {
  const [view, setView] = useState<PublicView>(() =>
    typeof window !== 'undefined' && window.location.hash === BOOKING_HASH ? 'booking' : 'links'
  );
  // True when WE created the current #booking history entry (lets the
  // in-flow back button pop it instead of stacking a duplicate entry)
  const pushedEntryRef = useRef(false);

  useEffect(() => {
    const onPopState = () => {
      const onBooking = window.location.hash === BOOKING_HASH;
      setView(onBooking ? 'booking' : 'links');
      // If Back/Forward landed ON the booking entry, the entry beneath it
      // is our own links entry — safe for exitBooking to pop
      pushedEntryRef.current = onBooking;
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const openBooking = () => {
    window.history.pushState(null, '', `${basePath()}${BOOKING_HASH}`);
    pushedEntryRef.current = true;
    setView('booking');
  };

  const exitBooking = () => {
    if (pushedEntryRef.current) {
      // The popstate handler flips the view and clears the flag
      window.history.back();
    } else {
      window.history.replaceState(null, '', basePath());
      setView('links');
    }
  };

  return { view, openBooking, exitBooking };
}
